import { useState, useCallback } from 'react'
import { Previewer } from 'pagedjs'
import dayjs from 'dayjs'

// Paged.js print styles - embedded as string for print window
const pagedPrintStyles = `
/* Paged.js Print Styles for History Drawer */

/* Page setup */
@page {
  size: A4;
  margin: 20mm;
  
  /* Running header with patient name */
  @top-center {
    content: "Patient History";
    font-size: 10px;
    color: #64748b;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    font-weight: 500;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 4px;
    margin-bottom: 8px;
  }
  
  /* Running footer with page numbers */
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font-size: 10px;
    color: #64748b;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    font-weight: 500;
    border-top: 1px solid #e5e7eb;
    padding-top: 4px;
    margin-top: 8px;
  }
}

/* Visit section - start new page for each visit */
.visit-section {
  page-break-before: always;
  page-break-after: avoid;
}

.visit-section:first-child {
  page-break-before: auto;
}

/* Report items - try to keep together */
.report-item {
  page-break-inside: avoid;
  margin-bottom: 16px;
}

/* Ensure headings don't break awkwardly */
h1, h2, h3, h4, h5, h6 {
  page-break-after: avoid;
  page-break-inside: avoid;
}

/* Tables should try to stay together */
table {
  page-break-inside: avoid;
}

/* Images should stay with their content */
img {
  page-break-inside: avoid;
  max-width: 100%;
  height: auto;
}

/* Prevent orphaned content */
p, li {
  orphans: 3;
  widows: 3;
}

/* Print-specific styling */
@media print {
  body {
    background: white !important;
    color: black !important;
    visibility: visible !important;
    display: block !important;
  }
  
  /* Ensure paged content container is visible in print */
  #paged-content {
    visibility: visible !important;
  }
  
  /* Hide UI elements */
  .hide-on-print-only,
  button,
  [class*="MuiButton"],
  [class*="MuiIconButton"],
  [class*="MuiDrawer"],
  [class*="MuiBackdrop"],
  [class*="MuiDialog"],
  [class*="MuiModal"] {
    display: none !important;
    visibility: hidden !important;
  }
  
  /* Hide connecting lines and UI decorations */
  [style*="right: 100%"],
  [style*="right:100%"],
  [style*="left: -"],
  [style*="opacity: 0"] {
    display: none !important;
    visibility: hidden !important;
  }
  
  /* Ensure proper spacing */
  .paged-print-content {
    padding: 0;
    margin: 0;
    visibility: visible !important;
    display: block !important;
  }
  
  /* Report type indicators */
  [data-paged-report-type="prescription"] {
    border-left: 3px solid #6779e3;
    padding-left: 12px;
  }
  
  [data-paged-report-type="therapy"] {
    border-left: 3px solid #10b981;
    padding-left: 12px;
  }
  
  [data-paged-report-type="screening"] {
    border-left: 3px solid #f59e0b;
    padding-left: 12px;
  }

  /* Rich text formatting (moved here; inline style tags are stripped in print clone) */
  .free-form-rich-text-view.tiptap-content { max-width: 100%; overflow: visible; }
  .free-form-rich-text-view.tiptap-content p { margin: 0 0 0.8em 0; line-height: 1.6; text-align: left; }
  .free-form-rich-text-view.tiptap-content p:last-child { margin-bottom: 0; }
  .free-form-rich-text-view.tiptap-content p:empty { min-height: 1.6em; margin: 0 0 0.8em 0; }
  .free-form-rich-text-view.tiptap-content ul,
  .free-form-rich-text-view.tiptap-content ol { margin: 0.8em 0; padding-left: 1.5em; line-height: 1.6; }
  .free-form-rich-text-view.tiptap-content li { margin-bottom: 0.4em; }
  .free-form-rich-text-view.tiptap-content strong,
  .free-form-rich-text-view.tiptap-content b { font-weight: 600; }
  .free-form-rich-text-view.tiptap-content em,
  .free-form-rich-text-view.tiptap-content i { font-style: italic; }
  .free-form-rich-text-view.tiptap-content u { text-decoration: underline; }
  .free-form-rich-text-view.tiptap-content h1,
  .free-form-rich-text-view.tiptap-content h2,
  .free-form-rich-text-view.tiptap-content h3,
  .free-form-rich-text-view.tiptap-content h4,
  .free-form-rich-text-view.tiptap-content h5,
  .free-form-rich-text-view.tiptap-content h6 { margin: 1em 0 0.5em 0; line-height: 1.3; font-weight: 600; }
  .free-form-rich-text-view.tiptap-content h1 { font-size: 1.3em; }
  .free-form-rich-text-view.tiptap-content h2 { font-size: 1.2em; }
  .free-form-rich-text-view.tiptap-content h3 { font-size: 1.1em; }
  .free-form-rich-text-view.tiptap-content blockquote { margin: 0.8em 0; padding-left: 1em; border-left: 3px solid #ccc; font-style: italic; color: #555; }
  .free-form-rich-text-view.tiptap-content code { background-color: #f4f4f4; padding: 0.1em 0.3em; border-radius: 3px; font-size: 0.9em; }
}
`

/**
 * Hook for printing history drawer reports using paged.js
 * Collects all reports from the drawer, prepares them for printing,
 * and uses paged.js Previewer for better pagination control
 */
export default function usePagedPrintHistory() {
  const [isPrinting, setIsPrinting] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Collect all report elements from the history drawer or main page
   * Looks for prescription, therapy, and screening reports
   */
  const collectAllReports = useCallback(() => {
    try {
      // First, try to find reports in the drawer (mobile view)
      let drawerContent = document.querySelector('[class*="MuiDrawer-paper"]')
      
      // If no drawer, try to find reports in the main page (desktop view)
      if (!drawerContent) {
        // Look for the Patient History section in the main page
        const historySection = document.querySelector('[data-paged-report-type]')?.closest('div[style*="maxWidth"]')?.parentElement
        if (historySection) {
          drawerContent = historySection
        } else {
          // Fallback: search the entire document body
          drawerContent = document.body
        }
      }

      // Find all report containers with data attributes
      let reportContainers = drawerContent.querySelectorAll(
        '[data-paged-report-type]'
      )

      if (reportContainers.length === 0) {
        // Fallback: try to find reports by common selectors
        const fallbackReports = drawerContent.querySelectorAll(
          'div[style*="maxWidth: \'794px\'"], div[style*="max-width: 794px"]'
        )
        if (fallbackReports.length > 0) {
          return Array.from(fallbackReports)
        }
        
        // Last resort: search entire document
        reportContainers = document.querySelectorAll('[data-paged-report-type]')
      }

      const reports = Array.from(reportContainers)
      
      if (reports.length === 0) {
        console.warn('collectAllReports: No reports found', {
          hasDrawer: !!document.querySelector('[class*="MuiDrawer-paper"]'),
          bodyReports: document.querySelectorAll('[data-paged-report-type]').length
        })
      }
      
      return reports
    } catch (err) {
      console.error('Error collecting reports:', err)
      return []
    }
  }, [])

  /**
   * Prepare content for paged.js by cloning and cleaning elements
   */
  const prepareContentForPaged = useCallback((reportElements) => {
    if (!reportElements || reportElements.length === 0) {
      console.warn('prepareContentForPaged: No report elements provided')
      return null
    }

    // Filter out null/undefined elements
    const validElements = reportElements.filter(el => el && el.nodeType === Node.ELEMENT_NODE)
    
    if (validElements.length === 0) {
      console.warn('prepareContentForPaged: No valid report elements found')
      return null
    }

    // Create a container for the print content
    const printContainer = document.createElement('div')
    printContainer.className = 'paged-print-content'
    printContainer.style.cssText = 'width: 210mm; background: white; padding: 0; margin: 0;'

    // Elements to remove from cloned content - be very specific to avoid removing report content
    // We'll handle removal manually to be more careful
    // Don't use this array - we'll remove elements manually below
    const selectorsToRemove = [] // Empty - we'll handle removal manually

    // Group reports by visit date
    const visitSections = new Map()

    validElements.forEach((element, index) => {
      // Debug: Log what we're cloning BEFORE cloning
      console.log('Cloning element (before):', {
        tagName: element.tagName,
        className: element.className,
        dataAttrs: {
          reportType: element.getAttribute('data-paged-report-type'),
          visitDate: element.getAttribute('data-paged-visit-date'),
        },
        innerHTMLLength: element.innerHTML?.length || 0,
        childrenCount: element.children.length,
        hasViewOnly: !!element.querySelector('[class*="ViewOnly"], [class*="prescription-page"], [class*="PMP"]'),
        textContentLength: element.textContent?.trim().length || 0
      })
      
      // Clone the element
      const cloned = element.cloneNode(true)

      // Remove embedded style tags generated by rich-text view components.
      // These tags can leak as printable text during paged rendering.
      let removedRichTextStyleTags = 0
      const embeddedRichTextStyleNodes = cloned.querySelectorAll(
        '.assessment-report-section style, .free-form-rich-text-view + style, .free-form-rich-text-view ~ style'
      )
      embeddedRichTextStyleNodes.forEach((styleNode) => {
        removedRichTextStyleTags += 1
        styleNode.remove()
      })
      
      // Debug: Log what we got AFTER cloning
      console.log('Cloned element (after):', {
        innerHTMLLength: cloned.innerHTML?.length || 0,
        childrenCount: cloned.children.length,
        hasViewOnly: !!cloned.querySelector('[class*="ViewOnly"], [class*="prescription-page"], [class*="PMP"]'),
        textContentLength: cloned.textContent?.trim().length || 0
      })

      // Remove UI elements (but preserve report content)
      // Be VERY conservative - only remove specific button/icon elements, not containers
      
      // First, remove only actual button elements (not containers)
      const buttons = cloned.querySelectorAll('button, [role="button"]')
      buttons.forEach((el) => {
        // Only remove if it's clearly a UI button (has MUI classes or is an icon button)
        const isUIButton = el.classList.contains('MuiButton') || 
                          el.classList.contains('MuiIconButton') ||
                          el.classList.contains('MuiButtonBase-root') ||
                          el.querySelector('svg[class*="MuiSvgIcon"]') // Has an icon
        if (isUIButton) {
          el.remove()
        }
      })
      
      // Remove elements with hide-on-print-only class, but ONLY if they're clearly UI containers
      // (not the main content wrapper)
      const hideOnPrintElements = cloned.querySelectorAll('.hide-on-print-only')
      hideOnPrintElements.forEach((el) => {
        // Check if this element contains actual report content
        const hasReportContent = el.querySelector('[class*="prescription-page"], [class*="PMP"], [class*="A4"], [class*="prescription"], [class*="ViewOnly"]')
        // Check if it's just a button container (has buttons but no report content)
        const hasButtons = el.querySelector('button, [class*="MuiButton"], [class*="MuiIconButton"]')
        const isButtonContainer = hasButtons && !hasReportContent
        // Only remove if it's clearly a UI element container (like a button wrapper) and not report content
        if (isButtonContainer) {
          console.log('Removing hide-on-print-only button container')
          el.remove()
        } else if (!hasReportContent && el.children.length === 0 && !el.textContent?.trim()) {
          // Only remove empty elements with no content
          console.log('Removing empty hide-on-print-only element')
          el.remove()
        } else {
          console.log('Keeping hide-on-print-only element (has content):', {
            hasReportContent: !!hasReportContent,
            hasButtons: !!hasButtons,
            childrenCount: el.children.length,
            textContentLength: el.textContent?.trim().length || 0
          })
        }
      })
      
      // Debug: Check content after removing hide-on-print-only
      console.log('After removing hide-on-print-only:', {
        textContentLength: cloned.textContent?.trim().length || 0,
        childrenCount: cloned.children.length,
        hasPrescription: !!cloned.querySelector('[class*="prescription-page"], [class*="PMP"], [class*="A4"]')
      })
      
      // Remove absolutely positioned elements that are UI decorations
      // Be very careful - only remove elements that are clearly UI decorations, not content
      const allElements = Array.from(cloned.querySelectorAll('*'))
      allElements.forEach((el) => {
        const style = el.getAttribute('style') || ''
        const isFixed = style.includes('position: fixed') || style.includes('position:fixed')
        const isAbsolute = style.includes('position: absolute') || style.includes('position:absolute')
        
        // Only remove if it's clearly a UI decoration (connecting lines, badges, etc.)
        if (isAbsolute || isFixed) {
          // Check if it's a connecting line or badge (positioned outside the content area)
          // AND doesn't contain actual content
          const isUIDecoration = (style.includes('right: 100%') || 
                                 style.includes('right:100%') ||
                                 style.includes('left: -') ||
                                 style.includes('opacity: 0') ||
                                 style.includes('opacity:0')) &&
                                !el.textContent?.trim() && // No text content
                                el.children.length === 0 // No children (just a decoration)
          
          if (isUIDecoration) {
            el.remove()
          }
        }
        
        // Remove elements with page-related text that are likely UI indicators
        // But only if they're small and don't contain actual content
        const text = (el.textContent || '').trim().toLowerCase()
        if (text.includes('page') && text.match(/page\s*\.?\s*\d*/) && text.length < 20) {
          // Only remove if it's a small UI indicator, not actual content
          const hasRealContent = el.querySelector('[class*="prescription"], [class*="therapy"], [class*="screening"]')
          if (!hasRealContent) {
            el.remove()
          }
        }
      })
      
      // Normalize positioning for paged.js compatibility
      // Convert absolute/fixed positioned elements to relative or static if they're content
      const positionedElements = cloned.querySelectorAll('[style*="position"]')
      positionedElements.forEach((el) => {
        const style = el.getAttribute('style') || ''
        // If it's not a UI decoration, convert to relative positioning
        if (style.includes('position: absolute') && 
            !style.includes('right: 100%') && 
            !style.includes('left: -') &&
            !style.includes('opacity: 0')) {
          // Keep the element but make it relatively positioned for better paged.js handling
          const newStyle = style.replace(/position:\s*absolute/gi, 'position: relative')
          el.setAttribute('style', newStyle)
        }
      })

      // Get visit date from data attribute or parent
      const visitDate =
        element.getAttribute('data-paged-visit-date') ||
        element.closest('[data-visit-date]')?.getAttribute('data-visit-date') ||
        `visit-${index}`

      // Add visit section wrapper if needed
      if (!visitSections.has(visitDate)) {
        const visitSection = document.createElement('div')
        visitSection.className = 'visit-section'
        visitSection.setAttribute('data-visit-date', visitDate)
        visitSections.set(visitDate, visitSection)
      }

      // Check if cloned element has any visible content after cleaning
      const hasVisibleContent = cloned.children.length > 0 || 
                                cloned.textContent?.trim().length > 0 ||
                                cloned.querySelector('img, svg, canvas') ||
                                cloned.querySelector('[class*="prescription-page"], [class*="PMP"], [class*="A4"]')
      
      if (!hasVisibleContent) {
        console.warn('Element has no visible content after cleaning:', {
          tagName: element.tagName,
          className: element.className,
          innerHTMLLength: cloned.innerHTML?.length || 0,
          childrenCount: cloned.children.length,
          textContentLength: cloned.textContent?.trim().length || 0,
          innerHTMLPreview: cloned.innerHTML?.substring(0, 200)
        })
        // Don't skip - maybe the content is there but not detected
        // return // Skip this element
      }

      // Add report item wrapper
      const reportItem = document.createElement('div')
      reportItem.className = 'report-item'

      // Detect report type
      const reportType =
        element.getAttribute('data-paged-report-type') ||
        (element.querySelector('[class*="prescription"]') ? 'prescription' : null) ||
        (element.querySelector('[class*="therapy"]') ? 'therapy' : null) ||
        (element.querySelector('[class*="screening"]') ? 'screening' : null) ||
        'unknown'

      reportItem.setAttribute('data-paged-report-type', reportType)
      reportItem.appendChild(cloned)
      visitSections.get(visitDate).appendChild(reportItem)
    })

    // Add all visit sections to container
    visitSections.forEach((section) => {
      printContainer.appendChild(section)
    })

    // Verify we have content
    if (printContainer.children.length === 0) {
      console.warn('prepareContentForPaged: No content sections created')
      return null
    }

    return printContainer
  }, [])

  /**
   * Print all reports using paged.js
   */
  const printWithPaged = useCallback(async () => {
    if (isPrinting) {
      return
    }

    setIsPrinting(true)
    setError(null)

    try {
      // Collect all reports
      const reportElements = collectAllReports()

      if (reportElements.length === 0) {
        throw new Error('No reports found to print')
      }

      // Prepare content
      const printContent = prepareContentForPaged(reportElements)

      if (!printContent) {
        throw new Error('Failed to prepare content for printing')
      }

      // Create a temporary container in the document
      const tempContainer = document.createElement('div')
      tempContainer.className = 'paged-print-container'
      tempContainer.style.cssText =
        'position: absolute; left: -9999px; top: -9999px; visibility: hidden; width: 210mm; background: white;'
      document.body.appendChild(tempContainer)
      tempContainer.appendChild(printContent)

      // Create print window
      const printWindow = window.open('', '_blank', 'width=800,height=600')

      if (!printWindow) {
        throw new Error('Failed to open print window')
      }

      // Get inline styles from current document
      const inlineStyles = Array.from(document.querySelectorAll('style'))
        .map((style) => style.innerHTML || '')
        .join('\n')

      // Write HTML structure
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Patient History - Print</title>
            <meta charset="utf-8">
            <meta name="application-name" content="AUMHUM">
            <style>
              ${pagedPrintStyles}
              
              ${inlineStyles}
              
              * {
                box-sizing: border-box;
              }
              
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                height: auto !important;
                background: white !important;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                overflow: visible !important;
              }
              
              ::-webkit-scrollbar {
                display: none !important;
              }
              
              * {
                scrollbar-width: none !important;
                -ms-overflow-style: none !important;
              }
            </style>
          </head>
          <body>
            <div id="paged-content"></div>
          </body>
        </html>
      `)

      printWindow.document.close()

      // Wait for window to load
      await new Promise((resolve) => {
        if (printWindow.document.readyState === 'complete') {
          resolve()
        } else {
          printWindow.addEventListener('load', resolve, { once: true })
        }
      })

      // Get the content container
      const contentContainer = printWindow.document.getElementById('paged-content')

      // Clone the prepared content to the print window
      const clonedContent = printContent.cloneNode(true)
      
      // Debug: Check if content has any visible elements
      console.log('Print content check (single report):', {
        hasChildren: clonedContent.children.length > 0,
        textContentLength: clonedContent.textContent?.trim().length || 0,
        textContentPreview: clonedContent.textContent?.substring(0, 200),
        innerHTMLLength: clonedContent.innerHTML?.length || 0,
        hasPrescription: !!clonedContent.querySelector('[class*="prescription"], [class*="PMP"], [class*="ViewOnly"]')
      })
      
      if (clonedContent.children.length === 0 && !clonedContent.textContent?.trim()) {
        console.error('No content to print!', {
          originalContent: printContent.innerHTML?.substring(0, 500),
          printContentChildren: printContent.children.length
        })
        throw new Error('No content found in prepared print content')
      }
      
      // Ensure content container is visible
      contentContainer.style.cssText = `
        visibility: visible !important;
        display: block !important;
        width: 100% !important;
        min-height: 100px !important;
        background: white !important;
        color: black !important;
        padding: 0 !important;
        margin: 0 !important;
      `
      
      // Ensure cloned content is visible
      clonedContent.style.cssText = `
        visibility: visible !important;
        display: block !important;
        width: 100% !important;
        background: white !important;
        color: black !important;
      `
      
      contentContainer.appendChild(clonedContent)
      
      // Force a reflow to ensure content is rendered
      contentContainer.offsetHeight

      const _inspectLargestSvg = (doc) => {
        const svgs = Array.from(doc.querySelectorAll('svg'))
        let largest = null
        svgs.forEach((svg) => {
          const r = svg.getBoundingClientRect()
          const area = (r.width || 0) * (r.height || 0)
          if (!largest || area > largest.area) {
            largest = {
              area,
              width: r.width || 0,
              height: r.height || 0,
              className: svg.className?.baseVal || svg.className || null,
              parentTag: svg.parentElement?.tagName || null,
              parentClass: svg.parentElement?.className || null,
            }
          }
        })
        return { count: svgs.length, largest }
      }

      
      // Wait for content to render
      await new Promise(resolve => setTimeout(resolve, 100))

      // Initialize paged.js Previewer
      const paged = new Previewer()
      
      // Suppress console warnings from paged.js during preview
      const originalWarn = console.warn
      console.warn = (...args) => {
        // Suppress "Unable to layout item" warnings from paged.js
        if (args[0] && typeof args[0] === 'string' && args[0].includes('Unable to layout item')) {
          return // Suppress this specific warning
        }
        originalWarn.apply(console, args)
      }
      
      // Wait for paged.js to process with error handling
      let flow
      try {
        flow = await paged.preview(clonedContent, [], contentContainer)
        console.log('Paged.js rendered', flow.total, 'pages')
        const pagedPagesRoot =
          contentContainer.querySelector('.pagedjs_pages') ||
          contentContainer.querySelector('.pagedjs_page')?.parentElement ||
          null
        if (pagedPagesRoot) {
          // Keep only paged output; remove original source content to avoid duplicate print.
          Array.from(contentContainer.childNodes).forEach((node) => {
            if (node !== pagedPagesRoot) {
              node.parentNode?.removeChild(node)
            }
          })
        }
        
        // Keep paged.js visibility semantics intact.
        // Forcing hidden nodes to display:block makes internal layout artifacts
        // (including <style> tag text) visible and duplicates report content.
        const pagedElements = contentContainer.querySelectorAll('*')
        pagedElements.forEach((el) => {
          // Never touch non-visual tags.
          if (['STYLE', 'SCRIPT', 'TEMPLATE', 'NOSCRIPT', 'META', 'LINK'].includes(el.tagName)) {
            return
          }
          const computedStyle = printWindow.getComputedStyle(el)
          
          // Remove any connecting lines or UI decorations (vertical lines)
          const style = el.getAttribute('style') || ''
          const isVerticalLine = (
            (style.includes('right: 100%') || style.includes('right:100%') || 
             style.includes('left: -') || style.includes('opacity: 0')) ||
            (computedStyle.width && parseFloat(computedStyle.width) < 10 && 
             computedStyle.height && parseFloat(computedStyle.height) > 100) ||
            (computedStyle.borderLeftWidth && parseFloat(computedStyle.borderLeftWidth) > 0 &&
             !el.textContent?.trim() && el.children.length === 0)
          )
          
          if (isVerticalLine && !el.textContent?.trim() && el.children.length === 0) {
            // This is likely a UI decoration (vertical line), remove it
            el.remove()
          }
        })
        
        // Ensure the content container itself is visible
        contentContainer.style.cssText = `
          visibility: visible !important;
          display: block !important;
          width: 100% !important;
          background: white !important;
          color: black !important;
          padding: 0 !important;
          margin: 0 !important;
        `
        
        // Make sure body and html are visible
        printWindow.document.body.style.cssText = `
          visibility: visible !important;
          display: block !important;
          background: white !important;
          color: black !important;
        `
        printWindow.document.documentElement.style.cssText = `
          visibility: visible !important;
          display: block !important;
        `

        
      } catch (layoutError) {
        console.warn('Paged.js layout error (continuing anyway):', layoutError)
        // Continue even if there's a layout error - the content should still be printable
        flow = { total: 1 } // Default to 1 page
      } finally {
        // Restore console.warn
        console.warn = originalWarn
      }

      // Wait a bit for rendering to complete
      await new Promise((resolve) => setTimeout(resolve, 500))
      
      // Final check: ensure content is visible before printing
      const finalCheck = contentContainer.querySelector('*')
      if (finalCheck) {
        const finalStyle = printWindow.getComputedStyle(finalCheck)
        console.log('Final visibility check:', {
          hasContent: !!finalCheck,
          display: finalStyle.display,
          visibility: finalStyle.visibility,
          textContent: contentContainer.textContent?.substring(0, 100)
        })
        
        // Force visibility one more time
        contentContainer.style.visibility = 'visible'
        contentContainer.style.display = 'block'
        printWindow.document.body.style.visibility = 'visible'
        printWindow.document.body.style.display = 'block'
      }

      // Trigger print
      printWindow.focus()
      
      // Wait for print dialog to potentially close
      const handleAfterPrint = () => {
        try {
          if (printWindow && !printWindow.closed) {
            printWindow.close()
          }
          if (tempContainer && tempContainer.parentNode === document.body) {
            document.body.removeChild(tempContainer)
          }
        } catch (cleanupError) {
          console.warn('Error during cleanup:', cleanupError)
        } finally {
          setIsPrinting(false)
          window.removeEventListener('afterprint', handleAfterPrint)
        }
      }
      
      window.addEventListener('afterprint', handleAfterPrint)
      printWindow.print()
      
      // Fallback cleanup in case afterprint doesn't fire
      setTimeout(() => {
        try {
          if (printWindow && !printWindow.closed) {
            printWindow.close()
          }
          if (tempContainer && tempContainer.parentNode === document.body) {
            document.body.removeChild(tempContainer)
          }
        } catch (cleanupError) {
          console.warn('Error during fallback cleanup:', cleanupError)
        } finally {
          setIsPrinting(false)
        }
      }, 5000)
    } catch (err) {
      console.error('Error printing with paged.js:', err)
      setError(err.message)
      setIsPrinting(false)
      
      // Fallback to basic print
      try {
        window.print()
      } catch (fallbackErr) {
        console.error('Fallback print also failed:', fallbackErr)
      }
    }
  }, [isPrinting, collectAllReports, prepareContentForPaged])

  /**
   * Print a single report element using paged.js
   */
  const printSingleReport = useCallback(async (reportElement) => {
    const reportType = reportElement?.getAttribute?.('data-paged-report-type') || 'unknown'
    if (isPrinting || !reportElement) {
      return
    }


    setIsPrinting(true)
    setError(null)

    try {
      // Verify element exists and is in the DOM
      if (!reportElement || !reportElement.parentNode) {
        throw new Error('Report element not found or not in DOM')
      }

      if (reportType === 'prescription') {
        const rxScope = reportElement.closest('[data-paged-report-type="prescription"]') || reportElement
        const _candidateRoots = [
          reportElement.querySelector('.d-flex.flex-column.gap-2'),
          reportElement.querySelector('.w-100'),
          reportElement,
        ].filter(Boolean)
        const preferredRxRoot = _candidateRoots.find(
          (el) => !el.classList?.contains('hide-on-print-only')
        ) || _candidateRoots[0]
        const allPages = Array.from(preferredRxRoot.querySelectorAll('.prescription-page'))
        const visiblePages = allPages.filter((page) => {
          const s = window.getComputedStyle(page)
          return s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0'
        })
        const allA4Nodes = Array.from(preferredRxRoot.querySelectorAll('[id^="a4-"], .A4PageContainer'))
        const visibleA4Nodes = allA4Nodes.filter((a4) => {
          const s = window.getComputedStyle(a4)
          return s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0'
        })

        let sortedPages = []
        const pagesByNumber = new Map()
        visiblePages.forEach((page) => {
          const pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page')
          if (!pageNum) return
          if (!pagesByNumber.has(pageNum)) pagesByNumber.set(pageNum, page)
        })
        sortedPages = Array.from(pagesByNumber.entries())
          .sort((a, b) => (parseInt(a[0], 10) || 0) - (parseInt(b[0], 10) || 0))
          .map(([, page]) => page)

        // Some history prescriptions render [id^="a4-*"] but no .prescription-page wrappers.
        if (sortedPages.length === 0 && visibleA4Nodes.length > 0) {
          const byA4Id = new Map()
          visibleA4Nodes.forEach((a4) => {
            const id = a4.getAttribute('id') || `a4-fallback-${byA4Id.size}`
            const wrapper =
              a4.closest('.prescription-page') ||
              a4.closest('.preview-page-container') ||
              a4.closest('.A4PageMeasureComponentWrapperContainer') ||
              a4
            if (!byA4Id.has(id)) byA4Id.set(id, wrapper)
          })
          sortedPages = Array.from(byA4Id.entries())
            .sort((a, b) => {
              const na = parseInt((a[0].match(/(\d+)/)?.[1] || '0'), 10) || 0
              const nb = parseInt((b[0].match(/(\d+)/)?.[1] || '0'), 10) || 0
              return na - nb
            })
            .map(([, node]) => node)
        }


        if (sortedPages.length > 0 || preferredRxRoot) {
          const detachedRootId = `history-single-prescription-root-${Date.now()}`
          const styleId = `history-single-prescription-style-${Date.now()}`
          const detachedRoot = document.createElement('div')
          detachedRoot.id = detachedRootId
          detachedRoot.setAttribute('data-history-single-prescription-root', 'true')
          detachedRoot.style.position = 'absolute'
          detachedRoot.style.left = '-100000px'
          detachedRoot.style.top = '0'
          detachedRoot.style.width = '794px'
          detachedRoot.style.background = '#fff'
          const rxClone = (preferredRxRoot || reportElement).cloneNode(true)
          if (rxClone.classList?.contains('hide-on-print-only')) {
            rxClone.classList.remove('hide-on-print-only')
          }
          const removeSelectors = [
            '.hide-on-print-only',
            'button',
            '[role="button"]',
            '[class*="MuiButton"]',
            '[class*="MuiIconButton"]',
            '.MuiSvgIcon-root',
          ]
          let removedNodeCount = 0
          removeSelectors.forEach((sel) => {
            const nodes = Array.from(rxClone.querySelectorAll(sel))
            removedNodeCount += nodes.length
            nodes.forEach((n) => n.remove())
          })
          // Remove connector/timeline decorative lines (left/right absolute lines).
          Array.from(rxClone.querySelectorAll('[style]')).forEach((el) => {
            const st = (el.getAttribute('style') || '').toLowerCase()
            const looksLikeConnector =
              (st.includes('position: absolute') || st.includes('position:absolute')) &&
              (st.includes('right: 100%') || st.includes('right:100%') || st.includes('left: -'))
            if (looksLikeConnector) {
              removedNodeCount += 1
              el.remove()
            }
          })
          if (sortedPages.length > 0) {
            detachedRoot.innerHTML = sortedPages.map((p) => p.outerHTML).join('')
          } else {
            detachedRoot.appendChild(rxClone)
          }
          document.body.appendChild(detachedRoot)
          const _firstPage =
            detachedRoot.querySelector('.prescription-page') ||
            detachedRoot.querySelector('[id^="a4-"]') ||
            detachedRoot.firstElementChild
          const _firstA4 =
            _firstPage?.querySelector?.('[id^="a4-"]') ||
            (_firstPage?.id?.startsWith('a4-') ? _firstPage : null)
          const _firstBody =
            _firstPage?.querySelector?.('[data-print-body="true"]') ||
            _firstPage?.querySelector?.('.A4PageBodyContainer') ||
            _firstPage?.querySelector?.('[class*="Body"]') ||
            _firstPage?.querySelector?.('[class*="body"]')
          const _firstFooter =
            _firstPage?.querySelector?.('[data-print-footer="true"]') ||
            _firstPage?.querySelector?.('[class*="Footer"]') ||
            _firstPage?.querySelector?.('[class*="footer"]')
          const _rect = (el) => {
            if (!el || !el.getBoundingClientRect) return null
            const r = el.getBoundingClientRect()
            return { x: r.x, y: r.y, w: r.width, h: r.height, top: r.top, bottom: r.bottom }
          }

          // Spacer experiment disabled: it caused overflow/extra-page regressions.
          // Keep natural flow (footer may appear right after content), but avoid page splits.

          const styleEl = document.createElement('style')
          styleEl.id = styleId
          styleEl.textContent = `
            @media print {
              body * { visibility: hidden !important; }
              body > * { display: none !important; }
              body > #${detachedRootId},
              body > #${detachedRootId} * {
                visibility: visible !important;
              }
              body > #${detachedRootId} {
                display: block !important;
                position: static !important;
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
                overflow: visible !important;
                background: #fff !important;
              }
              #${detachedRootId} .prescription-page {
                display: block !important;
                visibility: visible !important;
                page-break-after: always !important;
                break-after: page !important;
                margin: 0 auto !important;
                box-shadow: none !important;
                width: 794px !important;
                max-width: 794px !important;
                min-width: 794px !important;
                transform: none !important;
                zoom: 0.999 !important;
              }
              #${detachedRootId} .prescription-page:last-child {
                page-break-after: auto !important;
                break-after: auto !important;
              }
              #${detachedRootId} [id^="a4-"] {
                page-break-after: auto !important;
                break-after: auto !important;
                overflow: visible !important;
                display: block !important;
                width: 794px !important;
                max-width: 794px !important;
                min-width: 794px !important;
                min-height: 1122px !important;
                max-height: 1122px !important;
                transform: none !important;
                zoom: 0.999 !important;
              }
              #${detachedRootId} > [id^="a4-"] {
                page-break-after: always !important;
                break-after: page !important;
              }
              #${detachedRootId} > [id^="a4-"]:last-of-type {
                page-break-after: auto !important;
                break-after: auto !important;
              }
              #${detachedRootId} .hide-on-print-only,
              #${detachedRootId} button,
              #${detachedRootId} [class*="MuiButton"],
              #${detachedRootId} [class*="MuiIconButton"],
              #${detachedRootId} [role="button"],
              #${detachedRootId} svg[class*="MuiSvgIcon-root"] {
                display: none !important;
                visibility: hidden !important;
              }
              #${detachedRootId} [style*="right: 100%"],
              #${detachedRootId} [style*="right:100%"],
              #${detachedRootId} [style*="left: -"] {
                display: none !important;
                visibility: hidden !important;
              }
              @page { size: A4; margin: 0; }
            }
          `
          document.head.appendChild(styleEl)

          const _ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
          const _isSafari = /safari/i.test(_ua) && !/chrome|crios|android|fxios|firefox|edg/i.test(_ua)
          const _tempInlineStyles = []
          const _setTempInlineStyle = (el, prop, value) => {
            if (!el) return
            _tempInlineStyles.push([el, prop, el.style.getPropertyValue(prop), el.style.getPropertyPriority(prop)])
            el.style.setProperty(prop, value, 'important')
          }
          if (_isSafari) {
            const _pageNodes = Array.from(detachedRoot.querySelectorAll('.prescription-page'))
            const _a4Nodes = Array.from(detachedRoot.querySelectorAll('[id^="a4-"], .A4PageContainer'))
            _pageNodes.forEach((el) => {
              _setTempInlineStyle(el, 'width', '794px')
              _setTempInlineStyle(el, 'min-width', '794px')
              _setTempInlineStyle(el, 'max-width', '794px')
              _setTempInlineStyle(el, 'height', '1122px')
              _setTempInlineStyle(el, 'min-height', '1122px')
              _setTempInlineStyle(el, 'max-height', '1122px')
              _setTempInlineStyle(el, 'overflow', 'visible')
              _setTempInlineStyle(el, 'transform', 'none')
              _setTempInlineStyle(el, 'zoom', '0.999')
              _setTempInlineStyle(el, 'box-sizing', 'border-box')
            })
            _a4Nodes.forEach((el) => {
              _setTempInlineStyle(el, 'width', '794px')
              _setTempInlineStyle(el, 'min-width', '794px')
              _setTempInlineStyle(el, 'max-width', '794px')
              _setTempInlineStyle(el, 'height', '1122px')
              _setTempInlineStyle(el, 'min-height', '1122px')
              _setTempInlineStyle(el, 'max-height', '1122px')
              _setTempInlineStyle(el, 'overflow', 'visible')
              _setTempInlineStyle(el, 'transform', 'none')
              _setTempInlineStyle(el, 'zoom', '0.999')
              _setTempInlineStyle(el, 'box-sizing', 'border-box')
            })
          }


          let didCleanup = false
          const cleanup = (reason = 'unknown') => {
            if (didCleanup) return
            didCleanup = true
            const styleNode = document.getElementById(styleId)
            if (styleNode) styleNode.remove()
            while (_tempInlineStyles.length) {
              const [el, prop, val, prio] = _tempInlineStyles.pop()
              if (!el || !el.style) continue
              if (val) el.style.setProperty(prop, val, prio || '')
              else el.style.removeProperty(prop)
            }
            const rootNode = document.getElementById(detachedRootId)
            if (rootNode) rootNode.remove()
            setIsPrinting(false)
          }

          window.addEventListener('afterprint', () => cleanup('afterprint'), { once: true })
          const mq = window.matchMedia ? window.matchMedia('print') : null
          if (mq?.addEventListener) {
            const onChange = (evt) => {
              if (!evt?.matches) {
                cleanup('matchMedia-false')
                mq.removeEventListener('change', onChange)
              }
            }
            mq.addEventListener('change', onChange)
          }

          window.print()
          return
        }
      }

      // Prepare content for single report
      const printContent = prepareContentForPaged([reportElement])

      if (!printContent || printContent.children.length === 0) {
        throw new Error('Failed to prepare content for printing - no content found')
      }


      // Create a temporary container in the document
      const tempContainer = document.createElement('div')
      tempContainer.className = 'paged-print-container'
      tempContainer.style.cssText =
        'position: absolute; left: -9999px; top: -9999px; visibility: hidden; width: 210mm; background: white;'
      document.body.appendChild(tempContainer)
      tempContainer.appendChild(printContent)

      // Create print window
      const printWindow = window.open('', '_blank', 'width=800,height=600')

      if (!printWindow) {
        throw new Error('Failed to open print window')
      }


      // Get inline styles from current document
      const inlineStyles = Array.from(document.querySelectorAll('style'))
        .map((style) => style.innerHTML || '')
        .join('\n')

      // Write HTML structure
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Report - Print</title>
            <meta charset="utf-8">
            <meta name="application-name" content="AUMHUM">
            <style>
              ${pagedPrintStyles}
              
              ${inlineStyles}
              
              * {
                box-sizing: border-box;
              }
              
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                height: auto !important;
                background: white !important;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                overflow: visible !important;
              }
              
              ::-webkit-scrollbar {
                display: none !important;
              }
              
              * {
                scrollbar-width: none !important;
                -ms-overflow-style: none !important;
              }
            </style>
          </head>
          <body>
            <div id="paged-content"></div>
          </body>
        </html>
      `)

      printWindow.document.close()

      // Wait for window to load
      await new Promise((resolve) => {
        if (printWindow.document.readyState === 'complete') {
          resolve()
        } else {
          printWindow.addEventListener('load', resolve, { once: true })
        }
      })

      // Get the content container
      const contentContainer = printWindow.document.getElementById('paged-content')

      // Clone the prepared content to the print window
      const clonedContent = printContent.cloneNode(true)
      
      // Debug: Check if content has any visible elements
      console.log('Print content check (single report):', {
        hasChildren: clonedContent.children.length > 0,
        textContentLength: clonedContent.textContent?.trim().length || 0,
        textContentPreview: clonedContent.textContent?.substring(0, 200),
        innerHTMLLength: clonedContent.innerHTML?.length || 0,
        hasPrescription: !!clonedContent.querySelector('[class*="prescription"], [class*="PMP"], [class*="ViewOnly"]')
      })
      
      if (clonedContent.children.length === 0 && !clonedContent.textContent?.trim()) {
        console.error('No content to print!', {
          originalContent: printContent.innerHTML?.substring(0, 500),
          printContentChildren: printContent.children.length
        })
        throw new Error('No content found in prepared print content')
      }
      
      // Ensure content container is visible
      contentContainer.style.cssText = `
        visibility: visible !important;
        display: block !important;
        width: 100% !important;
        min-height: 100px !important;
        background: white !important;
        color: black !important;
        padding: 0 !important;
        margin: 0 !important;
      `
      
      // Ensure cloned content is visible
      clonedContent.style.cssText = `
        visibility: visible !important;
        display: block !important;
        width: 100% !important;
        background: white !important;
        color: black !important;
      `
      
      contentContainer.appendChild(clonedContent)
      
      // Force a reflow to ensure content is rendered
      contentContainer.offsetHeight
      
      // Wait for content to render
      await new Promise(resolve => setTimeout(resolve, 100))

      // Initialize paged.js Previewer
      const paged = new Previewer()
      
      // Suppress console warnings from paged.js during preview
      const originalWarn = console.warn
      console.warn = (...args) => {
        // Suppress "Unable to layout item" warnings from paged.js
        if (args[0] && typeof args[0] === 'string' && args[0].includes('Unable to layout item')) {
          return // Suppress this specific warning
        }
        originalWarn.apply(console, args)
      }
      
      // Wait for paged.js to process with error handling
      let flow
      try {
        const reportItemEl = clonedContent.querySelector('.report-item')
        const reportItemStyle = reportItemEl ? printWindow.getComputedStyle(reportItemEl) : null
        flow = await paged.preview(clonedContent, [], contentContainer)
        console.log('Paged.js rendered', flow.total, 'pages')
        const pagedPageNodes = Array.from(contentContainer.querySelectorAll('.pagedjs_page'))
        const emptyPagedPages = pagedPageNodes.filter((pageEl) => {
          const textLen = (pageEl.textContent || '').replace(/\s+/g, '').length
          const visualNodes = pageEl.querySelectorAll('img,svg,canvas,table,blockquote,p,li,h1,h2,h3,h4,h5,h6').length
          return textLen === 0 && visualNodes === 0
        }).length
        const pagedPagesRoot =
          contentContainer.querySelector('.pagedjs_pages') ||
          contentContainer.querySelector('.pagedjs_page')?.parentElement ||
          null
        if (pagedPagesRoot) {
          // Keep only paged output; remove original source content to avoid duplicate print.
          Array.from(contentContainer.childNodes).forEach((node) => {
            if (node !== pagedPagesRoot) {
              node.parentNode?.removeChild(node)
            }
          })
        }
        // Keep paged.js visibility semantics intact.
        // Forcing hidden nodes to display:block makes internal layout artifacts
        // (including <style> tag text) visible and duplicates report content.
        const pagedElements = contentContainer.querySelectorAll('*')
        pagedElements.forEach((el) => {
          // Never touch non-visual tags.
          if (['STYLE', 'SCRIPT', 'TEMPLATE', 'NOSCRIPT', 'META', 'LINK'].includes(el.tagName)) {
            return
          }
          const computedStyle = printWindow.getComputedStyle(el)
          
          // Remove any connecting lines or UI decorations (vertical lines)
          const style = el.getAttribute('style') || ''
          const isVerticalLine = (
            (style.includes('right: 100%') || style.includes('right:100%') || 
             style.includes('left: -') || style.includes('opacity: 0')) ||
            (computedStyle.width && parseFloat(computedStyle.width) < 10 && 
             computedStyle.height && parseFloat(computedStyle.height) > 100) ||
            (computedStyle.borderLeftWidth && parseFloat(computedStyle.borderLeftWidth) > 0 &&
             !el.textContent?.trim() && el.children.length === 0)
          )
          
          if (isVerticalLine && !el.textContent?.trim() && el.children.length === 0) {
            // This is likely a UI decoration (vertical line), remove it
            el.remove()
          }
        })
        
        // Ensure the content container itself is visible
        contentContainer.style.cssText = `
          visibility: visible !important;
          display: block !important;
          width: 100% !important;
          background: white !important;
          color: black !important;
          padding: 0 !important;
          margin: 0 !important;
        `
        
        // Make sure body and html are visible
        printWindow.document.body.style.cssText = `
          visibility: visible !important;
          display: block !important;
          background: white !important;
          color: black !important;
        `
        printWindow.document.documentElement.style.cssText = `
          visibility: visible !important;
          display: block !important;
        `
        
      } catch (layoutError) {
        console.warn('Paged.js layout error (continuing anyway):', layoutError)
        // When paged.js fails, rebuild the print DOM from the raw report node and
        // bypass paged wrappers/rules that can create blank pages in fallback print.
        contentContainer.innerHTML = ''
        const rawReportClone = reportElement.cloneNode(true)
        rawReportClone
          .querySelectorAll('style,script,template,noscript,meta,link')
          .forEach((node) => node.remove())
        rawReportClone
          .querySelectorAll('.hide-on-print-only,[class*="MuiIconButton"],button')
          .forEach((node) => node.remove())
        const fallbackReportHost = document.createElement('div')
        fallbackReportHost.className = 'single-report-fallback'
        fallbackReportHost.setAttribute('data-paged-report-type', reportType)
        fallbackReportHost.style.pageBreakInside = 'auto'
        fallbackReportHost.style.breakInside = 'auto'
        fallbackReportHost.appendChild(rawReportClone)
        contentContainer.appendChild(fallbackReportHost)
        flow = { total: 1 } // Default to 1 page for fallback path
      } finally {
        // Restore console.warn
        console.warn = originalWarn
      }

      // Wait a bit for rendering to complete
      await new Promise((resolve) => setTimeout(resolve, 500))
      
      // Final check: ensure content is visible before printing
      const finalCheck = contentContainer.querySelector('*')
      if (finalCheck) {
        const finalStyle = printWindow.getComputedStyle(finalCheck)
        console.log('Final visibility check:', {
          hasContent: !!finalCheck,
          display: finalStyle.display,
          visibility: finalStyle.visibility,
          textContent: contentContainer.textContent?.substring(0, 100)
        })
        
        // Force visibility one more time
        contentContainer.style.visibility = 'visible'
        contentContainer.style.display = 'block'
        printWindow.document.body.style.visibility = 'visible'
        printWindow.document.body.style.display = 'block'
      }

      // Trigger print
      printWindow.focus()
      
      // Wait for print dialog to potentially close
      const handleAfterPrint = () => {
        try {
          if (printWindow && !printWindow.closed) {
            printWindow.close()
          }
          if (tempContainer && tempContainer.parentNode === document.body) {
            document.body.removeChild(tempContainer)
          }
        } catch (cleanupError) {
          console.warn('Error during cleanup:', cleanupError)
        } finally {
          setIsPrinting(false)
          window.removeEventListener('afterprint', handleAfterPrint)
        }
      }
      
      window.addEventListener('afterprint', handleAfterPrint)
      printWindow.print()
      
      // Fallback cleanup in case afterprint doesn't fire
      setTimeout(() => {
        try {
          if (printWindow && !printWindow.closed) {
            printWindow.close()
          }
          if (tempContainer && tempContainer.parentNode === document.body) {
            document.body.removeChild(tempContainer)
          }
        } catch (cleanupError) {
          console.warn('Error during fallback cleanup:', cleanupError)
        } finally {
          setIsPrinting(false)
        }
      }, 5000)
    } catch (err) {
      console.error('Error printing single report with paged.js:', err)
      setError(err.message)
      setIsPrinting(false)
      
      // Fallback to basic print
      try {
        window.print()
      } catch (fallbackErr) {
        console.error('Fallback print also failed:', fallbackErr)
      }
    }
  }, [isPrinting, prepareContentForPaged])

  return {
    printWithPaged,
    printSingleReport,
    isPrinting,
    error,
  }
}

